import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import axios from 'axios';
import { CheckCircle2, Package, Truck, Home, MapPin, Loader2, SearchX, ExternalLink, XCircle } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TRANSIT_STATUSES = ['IN_TRANSIT', 'OUT_FOR_DELIVERY', 'REACHED_DELIVERY_PICKUP_POINT', 'FAILED_ATTEMPT'];

export const TrackOrderDetailPage = () => {
  const { orderId } = useParams();
  const { language } = useLanguage();
  const T = (fr, en) => (language === 'fr' ? fr : en);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    setLoading(true);
    axios
      .get(`${API}/track/${orderId}`)
      .then(({ data }) => setData(data))
      .catch(() => setNotFound(true))
      .finally(() => setLoading(false));
  }, [orderId]);

  const fmtDate = (iso) => {
    if (!iso) return null;
    try {
      return new Date(iso).toLocaleDateString(language === 'fr' ? 'fr-FR' : 'en-US', {
        day: 'numeric', month: 'long', year: 'numeric', hour: '2-digit', minute: '2-digit',
      });
    } catch {
      return iso;
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#FBF8F3] flex items-center justify-center" data-testid="track-detail-loading">
        <Loader2 className="w-8 h-8 animate-spin text-[#0F4C5C]" />
      </div>
    );
  }

  if (notFound) {
    return (
      <div className="min-h-screen bg-[#FBF8F3] flex items-center justify-center px-4" data-testid="track-detail-not-found">
        <Card className="max-w-md w-full border-[#0F4C5C]/10">
          <CardContent className="pt-8 pb-8 text-center">
            <SearchX className="w-12 h-12 text-[#0F4C5C]/40 mx-auto mb-4" />
            <h1 className="text-xl font-bold text-[#0F4C5C] mb-2">
              {T('Commande introuvable', 'Order not found')}
            </h1>
            <p className="text-sm text-muted-foreground mb-6">
              {T('Vérifiez le numéro de commande fourni dans votre email de confirmation.',
                 'Check the order number provided in your confirmation email.')}
            </p>
            <Link to="/track">
              <Button variant="outline">{T('Réessayer', 'Try again')}</Button>
            </Link>
          </CardContent>
        </Card>
      </div>
    );
  }

  const isCancelled = data.status === 'cancelled';
  const isDelivered = data.status === 'delivered';
  const isShipped = !!data.shipped_at || ['shipped', 'delivered'].includes(data.status);
  const inTransit = TRANSIT_STATUSES.includes(data.tracking?.status) || isDelivered;

  const steps = [
    {
      icon: CheckCircle2,
      label: T('Commande confirmée', 'Order confirmed'),
      done: true,
      date: fmtDate(data.created_at),
    },
    {
      icon: Package,
      label: T('Expédiée', 'Shipped'),
      done: isShipped,
      date: fmtDate(data.shipped_at),
    },
    {
      icon: Truck,
      label: T('En cours de livraison', 'In transit'),
      done: inTransit,
      date: inTransit && data.tracking?.message ? data.tracking.message : null,
    },
    {
      icon: Home,
      label: data.relay_point ? T('Disponible en point relais', 'Available at pickup point') : T('Livrée', 'Delivered'),
      done: isDelivered,
      date: fmtDate(data.delivered_at),
    },
  ];

  const currentIdx = steps.reduce((acc, s, i) => (s.done ? i : acc), 0);

  return (
    <div className="min-h-screen bg-[#FBF8F3] py-12 px-4" data-testid="track-detail-page">
      <div className="max-w-2xl mx-auto">
        <div className="mb-8">
          <p className="text-sm text-[#0F4C5C]/60 uppercase tracking-widest mb-1">
            {T('Suivi de commande', 'Order tracking')}
          </p>
          <div className="flex items-center gap-3 flex-wrap">
            <h1 className="text-3xl font-bold text-[#0F4C5C]" data-testid="track-order-number">
              #{data.order_id.slice(0, 8).toUpperCase()}
            </h1>
            <Badge className={isDelivered ? 'badge-success' : isCancelled ? 'bg-red-100 text-red-700' : 'badge-info'} data-testid="track-status-badge">
              {isCancelled ? T('Annulée', 'Cancelled')
                : isDelivered ? T('Livrée', 'Delivered')
                : isShipped ? T('Expédiée', 'Shipped')
                : data.status === 'pending_payment' ? T('En attente de paiement', 'Awaiting payment')
                : T('En préparation', 'Processing')}
            </Badge>
          </div>
          <p className="text-sm text-muted-foreground mt-2">
            {data.items_count} {T('article(s)', 'item(s)')} · {T('Destination', 'Destination')}: {data.destination.city}, {data.destination.country}
          </p>
        </div>

        {isCancelled ? (
          <Card className="border-red-200 bg-red-50">
            <CardContent className="pt-6 pb-6 flex items-center gap-3">
              <XCircle className="w-6 h-6 text-red-500 shrink-0" />
              <p className="text-sm text-red-700">
                {T('Cette commande a été annulée. Contactez-nous si vous avez des questions.',
                   'This order was cancelled. Contact us if you have any questions.')}
              </p>
            </CardContent>
          </Card>
        ) : (
          <Card className="border-[#0F4C5C]/10 shadow-sm">
            <CardContent className="pt-8 pb-8">
              <div className="space-y-0" data-testid="track-timeline">
                {steps.map((step, i) => {
                  const Icon = step.icon;
                  const isCurrent = i === currentIdx && !isDelivered;
                  return (
                    <div key={i} className="flex gap-4" data-testid={`track-step-${i}`}>
                      <div className="flex flex-col items-center">
                        <div className={`w-10 h-10 rounded-full flex items-center justify-center shrink-0 transition-colors ${
                          step.done ? 'bg-[#0F4C5C] text-white' : 'bg-slate-100 text-slate-400'
                        } ${isCurrent ? 'ring-4 ring-[#0F4C5C]/15' : ''}`}>
                          <Icon className="w-5 h-5" />
                        </div>
                        {i < steps.length - 1 && (
                          <div className={`w-0.5 flex-1 min-h-10 ${steps[i + 1].done ? 'bg-[#0F4C5C]' : 'bg-slate-200'}`} />
                        )}
                      </div>
                      <div className={`pb-8 ${i === steps.length - 1 ? 'pb-0' : ''}`}>
                        <p className={`font-semibold ${step.done ? 'text-[#0F4C5C]' : 'text-slate-400'}`}>{step.label}</p>
                        {step.date && <p className="text-xs text-muted-foreground mt-0.5">{step.date}</p>}
                        {isCurrent && !step.date && (
                          <p className="text-xs text-muted-foreground mt-0.5">{T('En cours…', 'In progress…')}</p>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </CardContent>
          </Card>
        )}

        {data.tracking?.tracking_number && (
          <Card className="border-[#0F4C5C]/10 mt-6" data-testid="track-carrier-card">
            <CardContent className="pt-6 pb-6 flex items-center justify-between gap-4 flex-wrap">
              <div>
                <p className="text-xs text-muted-foreground">{T('Numéro de suivi', 'Tracking number')} · {data.tracking.carrier}</p>
                <p className="font-mono font-bold text-[#0F4C5C] text-lg">{data.tracking.tracking_number}</p>
                {data.estimated_delivery && (
                  <p className="text-xs text-muted-foreground mt-1">
                    {T('Livraison estimée', 'Estimated delivery')}: {data.estimated_delivery}
                  </p>
                )}
              </div>
              {data.tracking.tracking_url && (
                <a href={data.tracking.tracking_url} target="_blank" rel="noreferrer">
                  <Button variant="outline" data-testid="track-carrier-link">
                    <ExternalLink className="w-4 h-4 mr-2" />
                    {T('Suivi transporteur', 'Carrier tracking')}
                  </Button>
                </a>
              )}
            </CardContent>
          </Card>
        )}

        {data.relay_point?.name && (
          <Card className="border-[#0F4C5C]/10 mt-6" data-testid="track-relay-card">
            <CardContent className="pt-6 pb-6 flex items-center gap-3">
              <MapPin className="w-5 h-5 text-[#0F4C5C] shrink-0" />
              <div>
                <p className="text-xs text-muted-foreground">{T('Point relais de retrait', 'Pickup point')}</p>
                <p className="font-semibold text-[#0F4C5C]">{data.relay_point.name}{data.relay_point.city ? ` — ${data.relay_point.city}` : ''}</p>
              </div>
            </CardContent>
          </Card>
        )}

        <p className="text-center text-xs text-muted-foreground mt-8">
          {T('Une question sur votre livraison ?', 'A question about your delivery?')}{' '}
          <Link to="/contact" className="underline text-[#0F4C5C]">{T('Contactez-nous', 'Contact us')}</Link>
        </p>
      </div>
    </div>
  );
};
